import { registerSW } from 'virtual:pwa-register';

/**
 * Service worker registration.
 *
 * `vite-plugin-pwa` is configured with `injectRegister: null`, so nothing
 * registers the worker unless this runs. That is deliberate, and it was also
 * briefly a bug: the worker was generated and orphaned, which meant the app had
 * no offline capability at all while looking exactly like it did.
 *
 * The reason for owning registration rather than letting the plugin inject it
 * is the update policy, which is a product decision rather than a build detail.
 *
 * **An update must never be applied to a running scorebook.** `autoUpdate`
 * swaps the worker under the open page. In most apps that is a papercut. Here
 * it can happen in the third quarter, to a volunteer holding an unsynced event
 * queue, and the cost is a game's data in front of parents. So the update is
 * offered and the caller decides, and the scorebook's answer is always "not
 * now" until the game is final.
 */

export interface ServiceWorkerHandlers {
  /**
   * A new version is waiting. Render a themed prompt and call `apply` if the
   * user accepts. Never call it without asking, and never during a live game.
   *
   * The themed prompt lands with the UI primitives. Until then the default is
   * to do nothing, which leaves the current version serving. That is the safe
   * direction: a stale app that works beats a fresh one that reloaded mid
   * possession.
   */
  /*
   * `apply` RETURNS its promise. It used to be `() => void` wrapping
   * `void applyUpdate(true)`, which threw the only handle on the outcome away:
   * `applyUpdate` is async, so a rejection escaped as an unhandled rejection and
   * the caller could not tell a reload that is coming from one that will never
   * arrive. The cross-model pass graded that P2 on the prompt's try/catch, which
   * could only ever have caught a synchronous throw.
   */
  readonly onUpdateAvailable?: (apply: () => void | Promise<void>) => void;
  /** The shell is cached and the app will now start with no network. */
  readonly onOfflineReady?: () => void;
}

export function registerServiceWorker(handlers: ServiceWorkerHandlers = {}): void {
  const applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      handlers.onUpdateAvailable?.(() => applyUpdate(true));
    },
    onOfflineReady() {
      handlers.onOfflineReady?.();
    },
  });
}
